import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ResponsiveTable, TableCellBadge, TableCellActions, TableCellText } from '@/components/ui/responsive-table';
import { 
  FileText, 
  Plus, 
  Send, 
  Eye, 
  Edit, 
  MoreVertical,
  Filter,
  Search,
  Calendar as CalendarIcon,
  Users,
  CheckCircle,
  AlertCircle,
  Clock,
  XCircle
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { StandardPageHeader } from '@/components/layout/StandardPageHeader';
import { PageContainer } from '@/components/layout/PageContainer';
import { Progress } from '@/components/ui/progress';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { DocumentRequestService } from '@/services/documentRequestService';
import { EmailService } from '@/services/emailService';
import { supabase } from '@/lib/supabase';
import CreateDocumentRequestModal from '@/components/admin/CreateDocumentRequestModal';
import { 
  DocumentRequestWithStats, 
  DocumentRequestStatus,
  DocumentRequestFilters 
} from '@/types/documentRequestTypes';

const statusConfig = {
  draft: { 
    color: 'bg-gray-100 text-gray-800', 
    icon: Clock, 
    label: 'Draft' 
  },
  active: { 
    color: 'bg-blue-100 text-blue-800', 
    icon: AlertCircle, 
    label: 'Active' 
  },
  completed: { 
    color: 'bg-green-100 text-green-800', 
    icon: CheckCircle, 
    label: 'Completed' 
  },
  cancelled: { 
    color: 'bg-red-100 text-red-800', 
    icon: XCircle, 
    label: 'Cancelled' 
  }
};

const DocumentRequests = () => {
  const { user } = useAuth();
  const organizationId = user?.user_metadata?.organization_id;
  
  // State
  const [requests, setRequests] = useState<DocumentRequestWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<DocumentRequestFilters>({});
  const [activeTab, setActiveTab] = useState<DocumentRequestStatus | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<DocumentRequestWithStats | null>(null);
  
  // Edit form state
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editDeadline, setEditDeadline] = useState<Date | undefined>();
  const [editStatus, setEditStatus] = useState<DocumentRequestStatus>('draft');
  const [isUpdating, setIsUpdating] = useState(false);
  
  const perPage = 20;

  // Fetch requests
  const fetchRequests = async () => {
    if (!organizationId) return;
    
    setLoading(true);
    try {
      const requestFilters: DocumentRequestFilters = {
        ...filters,
        search_query: searchQuery || undefined,
        status: activeTab === 'all' ? undefined : [activeTab]
      };

      const result = await DocumentRequestService.getDocumentRequests(
        organizationId, 
        requestFilters, 
        currentPage, 
        perPage
      );
      
      if (result.success && result.data) {
        setRequests(result.data.data);
        setTotalPages(result.data.total_pages);
      } else {
        throw new Error(result.error || 'Failed to fetch requests');
      }
    } catch (error) {
      console.error('Error fetching document requests:', error);
      toast.error('Failed to load document requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [organizationId, filters, activeTab, searchQuery, currentPage]);

  // Handle send request
  const handleSendRequest = async (requestId: string) => {
    try {
      const result = await DocumentRequestService.sendDocumentRequest(requestId);
      
      if (result.success) {
        toast.success(`Document request sent to ${result.sentCount} families`);
        fetchRequests(); // Refresh the list
      } else {
        toast.error(result.error || 'Failed to send request');
      }
    } catch (error) {
      console.error('Error sending request:', error);
      toast.error('Failed to send request');
    }
  };

  // Handle view details
  const handleViewDetails = (request: DocumentRequestWithStats) => {
    setSelectedRequest(request);
    setIsViewModalOpen(true);
  };

  // Handle edit request
  const handleEditRequest = (request: DocumentRequestWithStats) => {
    setSelectedRequest(request);
    // Populate form with current values
    setEditTitle(request.title);
    setEditDescription(request.description || '');
    setEditDeadline(request.deadline ? new Date(request.deadline) : undefined);
    setEditStatus(request.status);
    setIsEditModalOpen(true);
  };

  // Handle edit form submission
  const handleEditSubmit = async () => {
    if (!selectedRequest || !editTitle.trim()) {
      toast.error('Title is required');
      return;
    }

    setIsUpdating(true);
    try {
      const updates = {
        title: editTitle.trim(),
        description: editDescription.trim() || undefined,
        deadline: editDeadline ? editDeadline.toISOString().split('T')[0] : undefined,
        status: editStatus
      };

      const result = await DocumentRequestService.updateDocumentRequest(selectedRequest.id, updates);

      if (result.success) {
        toast.success('Document request updated successfully');
        setIsEditModalOpen(false);
        fetchRequests(); // Refresh the list
      } else {
        toast.error(result.error || 'Failed to update request');
      }
    } catch (error) {
      console.error('Error updating request:', error);
      toast.error('Failed to update request');
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle send reminder
  const handleSendReminder = async (requestId: string) => {
    try {
      // Get request details to find families who haven't submitted
      const requestDetails = await DocumentRequestService.getDocumentRequestDetails(requestId);
      if (!requestDetails.success || !requestDetails.data) {
        throw new Error('Failed to get request details');
      }

      const { request, families } = requestDetails.data;
      
      // Get organization details
      const { data: organization, error: orgError } = await supabase
        .from('organizations')
        .select('name')
        .eq('id', request.organization_id)
        .single();

      if (orgError) throw orgError;

      let sentCount = 0;
      const errors: string[] = [];

      // Calculate days until deadline for urgency
      const daysUntilDeadline = request.deadline 
        ? Math.ceil((new Date(request.deadline).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
        : 7; // Default to 7 days if no deadline

      // Send reminders to families who haven't submitted (status = 'pending')
      for (const family of families) {
        if (family.status === 'pending' && family.family) {
          try {
            // Get family's primary parent email (reuse logic from sendDocumentRequest)
            const { data: parentInvitations } = await supabase
              .from('parent_invitations')
              .select('email')
              .eq('family_id', family.family_id)
              .limit(1);

            let parentEmail: string | null = null;
            
            if (parentInvitations && parentInvitations.length > 0) {
              parentEmail = parentInvitations[0].email;
            } else {
              // Try to get from parents table with user profiles
              const { data: parents } = await supabase
                .from('parents')
                .select(`
                  user_id,
                  user_profiles!inner(email)
                `)
                .eq('family_id', family.family_id)
                .eq('is_primary', true)
                .limit(1);

              if (parents && parents.length > 0) {
                parentEmail = parents[0].user_profiles?.email;
              }
            }

            if (!parentEmail) {
              errors.push(`No email found for family ${family.family?.name}`);
              continue;
            }

            // Construct upload link
            const siteUrl = import.meta.env.VITE_SITE_URL || "https://www.hivetivity.com";
            const uploadLink = `${siteUrl}/parent-portal/documents?request=${requestId}`;

            // Send reminder email
            const emailResult = await EmailService.sendDocumentRequestReminder(
              parentEmail,
              family.family.name,
              request.title,
              request.deadline,
              daysUntilDeadline,
              uploadLink,
              organization.name
            );

            if (!emailResult.success) {
              throw new Error(emailResult.error || 'Failed to send email');
            }

            // Record reminder in database
            await supabase
              .from('request_reminders')
              .insert({
                request_id: requestId,
                family_id: family.family_id,
                reminder_type: daysUntilDeadline <= 1 ? 'final' : daysUntilDeadline <= 3 ? 'reminder_2' : 'reminder_1'
              });

            sentCount++;
          } catch (emailError) {
            errors.push(`Failed to send to ${family.family?.name}: ${emailError instanceof Error ? emailError.message : 'Unknown error'}`);
          }
        }
      }

      if (sentCount > 0) {
        toast.success(`Reminders sent to ${sentCount} families`);
      } else if (errors.length > 0) {
        toast.error(`Failed to send reminders: ${errors.join(', ')}`);
      } else {
        toast.info('No pending families found to send reminders to');
      }
    } catch (error) {
      console.error('Error sending reminder:', error);
      toast.error('Failed to send reminder');
    }
  };

  // Get completion percentage color
  const getCompletionColor = (percentage: number) => {
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  // Filter requests by tab
  const getTabCounts = () => {
    return {
      all: requests.length,
      draft: requests.filter(r => r.status === 'draft').length,
      active: requests.filter(r => r.status === 'active').length,
      completed: requests.filter(r => r.status === 'completed').length,
      cancelled: requests.filter(r => r.status === 'cancelled').length
    };
  };

  const tabCounts = getTabCounts();

  return (
    <div className="space-y-6">
      <StandardPageHeader
        title="Document Requests"
        subtitle="Manage and track parent document submissions"
        icon={FileText}
        actions={
          <Button onClick={() => setIsCreateModalOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Request
          </Button>
        }
      />
      
      <PageContainer>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Requests</p>
                <p className="text-2xl font-bold">{requests.length}</p>
              </div>
              <FileText className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active Requests</p>
                <p className="text-2xl font-bold">{tabCounts.active}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Completed</p>
                <p className="text-2xl font-bold">{tabCounts.completed}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Average Completion</p>
                <p className="text-2xl font-bold">
                  {requests.length > 0 
                    ? Math.round(requests.reduce((acc, r) => acc + r.stats.completion_percentage, 0) / requests.length)
                    : 0}%
                </p>
              </div>
              <Users className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter Controls */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search requests..."
                  aria-label="Search document requests"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div className="w-full md:w-48">
              <Select value={filters.created_by || 'all'} onValueChange={(value) => 
                setFilters(prev => ({ ...prev, created_by: value === 'all' ? undefined : value }))
              }>
                <SelectTrigger>
                  <div className="flex items-center">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Created by" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Creators</SelectItem>
                  {/* TODO: Add dynamic creator options */}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Status Tabs */}
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as DocumentRequestStatus | 'all')}>
        <TabsList className="grid grid-cols-5 mb-4">
          <TabsTrigger value="all">
            All
            <Badge variant="outline" className="ml-2">
              {tabCounts.all}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="draft">
            Draft
            <Badge variant="outline" className="ml-2">
              {tabCounts.draft}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="active">
            Active
            <Badge variant="outline" className="ml-2">
              {tabCounts.active}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="completed">
            Completed
            <Badge variant="outline" className="ml-2">
              {tabCounts.completed}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="cancelled">
            Cancelled
            <Badge variant="outline" className="ml-2">
              {tabCounts.cancelled}
            </Badge>
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value={activeTab} className="mt-0">
          {loading ? (
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-3">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : requests.length === 0 ? (
            <Card>
              <CardContent className="py-10 text-center">
                <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium mb-2">No Document Requests</h3>
                <p className="text-muted-foreground mb-4">
                  {searchQuery 
                    ? "No requests match your search criteria" 
                    : "Create your first document request to get started"}
                </p>
                <Button onClick={() => setIsCreateModalOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Request
                </Button>
              </CardContent>
            </Card>
          ) : (
            <ResponsiveTable
              data={requests}
              columns={[
                {
                  key: 'title',
                  header: 'Request',
                  priority: 'high',
                  cell: (request) => (
                    <TableCellText
                      primary={request.title}
                      secondary={request.description}
                    />
                  )
                },
                {
                  key: 'status',
                  header: 'Status',
                  priority: 'high',
                  cell: (request) => {
                    const statusInfo = statusConfig[request.status];
                    const StatusIcon = statusInfo.icon;
                    return (
                      <TableCellBadge variant="outline">
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {statusInfo.label}
                      </TableCellBadge>
                    );
                  }
                },
                {
                  key: 'families',
                  header: 'Families',
                  priority: 'medium',
                  cell: (request) => (
                    <TableCellText
                      primary={`${request.stats.total_families} families`}
                      secondary={`${request.stats.submitted_count} submitted`}
                    />  
                  )
                },
                {
                  key: 'progress',
                  header: 'Progress',
                  priority: 'high',
                  hideOnMobile: false,
                  cell: (request) => (
                    <div className="w-full">
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-sm font-medium ${getCompletionColor(request.stats.completion_percentage)}`}>
                          {Math.round(request.stats.completion_percentage)}%
                        </span>
                      </div>
                      <Progress 
                        value={request.stats.completion_percentage} 
                        className="h-2" 
                      />
                    </div>
                  )
                },
                {
                  key: 'deadline',
                  header: 'Deadline',
                  priority: 'medium',
                  cell: (request) => {
                    if (!request.deadline) {
                      return <span className="text-muted-foreground">No deadline</span>;
                    }
                    const isOverdue = new Date(request.deadline) < new Date();
                    const daysLeft = Math.ceil((new Date(request.deadline).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
                    return (
                      <TableCellText
                        primary={format(new Date(request.deadline), 'MMM d, yyyy')}
                        secondary={isOverdue ? 'Overdue' : `${daysLeft} days left`}
                      />
                    );
                  }
                },
                {
                  key: 'created_at',
                  header: 'Created',
                  priority: 'low',
                  hideOnMobile: true,
                  cell: (request) => (
                    <span className="text-sm text-muted-foreground">
                      {format(new Date(request.created_at), 'MMM d, yyyy')}
                    </span>
                  )
                },
                {
                  key: 'actions',
                  header: 'Actions',
                  priority: 'high',
                  cell: (request) => (
                    <TableCellActions>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleViewDetails(request)}>
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEditRequest(request)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit Request
                          </DropdownMenuItem>
                          {request.status === 'draft' && (
                            <DropdownMenuItem onClick={() => handleSendRequest(request.id)}>
                              <Send className="h-4 w-4 mr-2" />
                              Send to Families
                            </DropdownMenuItem>
                          )}
                          {request.status === 'active' && (
                            <DropdownMenuItem onClick={() => handleSendReminder(request.id)}>
                              <Send className="h-4 w-4 mr-2" />
                              Send Reminder
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCellActions>
                  )
                }
              ]}
              cardTitle={(request) => request.title}
              cardSubtitle={(request) => {
                const statusInfo = statusConfig[request.status];
                return statusInfo.label;
              }}
              cardActions={(request) => (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleViewDetails(request)}>
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleEditRequest(request)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  {request.status === 'draft' && (
                    <Button size="sm" onClick={() => handleSendRequest(request.id)}>
                      <Send className="h-4 w-4" />
                    </Button>
                  )}
                  {request.status === 'active' && (
                    <Button size="sm" onClick={() => handleSendReminder(request.id)}>
                      <Send className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              )}
              emptyMessage="No document requests found"
            />
          )}
        </TabsContent>
      </Tabs>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center space-x-2">
          <Button 
            variant="outline" 
            size="sm"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(prev => prev - 1)}
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {currentPage} of {totalPages}
          </span>
          <Button 
            variant="outline" 
            size="sm"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(prev => prev + 1)}
          >
            Next
          </Button>
        </div>
      )}

      {/* Create Document Request Modal */}
      <CreateDocumentRequestModal
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
        onSuccess={() => {
          fetchRequests();
          setIsCreateModalOpen(false);
        }}
      />

      {/* View Details Modal */}
      <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Document Request Details</DialogTitle>
          </DialogHeader>
          {selectedRequest ? (
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold">Title</h3>
                <p>{selectedRequest.title || 'No title'}</p>
              </div>
              {selectedRequest.description && (
                <div>
                  <h3 className="font-semibold">Description</h3>
                  <p>{selectedRequest.description}</p>
                </div>
              )}
              <div>
                <h3 className="font-semibold">Status</h3>
                <Badge className={statusConfig[selectedRequest.status]?.color || 'bg-gray-100 text-gray-800'}>
                  {statusConfig[selectedRequest.status]?.label || selectedRequest.status}
                </Badge>
              </div>
              {selectedRequest.deadline && (
                <div>
                  <h3 className="font-semibold">Deadline</h3>
                  <p>{format(new Date(selectedRequest.deadline), 'PPP')}</p>
                </div>
              )}
              <div>
                <h3 className="font-semibold">Progress</h3>
                <div className="flex items-center gap-2">
                  <Progress value={selectedRequest.stats?.completion_percentage || 0} className="flex-1" />
                  <span className="text-sm font-medium">{Math.round(selectedRequest.stats?.completion_percentage || 0)}%</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold">Total Families</h3>
                  <p>{selectedRequest.stats?.total_families || 0}</p>
                </div>
                <div>
                  <h3 className="font-semibold">Submitted</h3>
                  <p>{selectedRequest.stats?.submitted_count || 0}</p>
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                Created: {selectedRequest.created_at ? format(new Date(selectedRequest.created_at), 'PPP') : 'Unknown'}
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No request data available</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsViewModalOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Request Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Document Request</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="edit-title">Title *</Label>
              <Input
                id="edit-title"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                placeholder="Enter request title"
                required
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder="Provide detailed instructions about what documents are needed..."
                rows={3}
              />
            </div>

            {/* Deadline */}
            <div className="space-y-2">
              <Label>Deadline (Optional)</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !editDeadline && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {editDeadline ? format(editDeadline, "PPP") : "Select deadline"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={editDeadline}
                    onSelect={setEditDeadline}
                    disabled={(date) => date < new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Status */}
            <div className="space-y-2">
              <Label htmlFor="edit-status">Status</Label>
              <Select value={editStatus} onValueChange={(value) => setEditStatus(value as DocumentRequestStatus)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditSubmit} disabled={isUpdating}>
              {isUpdating ? 'Updating...' : 'Update Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </PageContainer>
    </div>
  );
};

export default DocumentRequests;